import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Notification } from '@schibsted-svp/react-ui';
import { createVideo, createVideoFromUri, createVideoWithoutFile } from 'store/video/upload/actions';
import { isAudioOrVideoType } from 'lib/file';
import { TABS } from './UploadDialog';

const uploadFormSchema = z
    .object({
        files: z
            .any()
            .optional()
            .refine((files) => files?.every((file: File) => isAudioOrVideoType(file)) ?? true, 'Unsupported file(s)'),
        fileUrl: z.union([z.literal(''), z.string().url()]).optional(),
        overlay: z.string().optional(),
        fileType: z.string().optional(),
        activeTab: z.string().optional(),
        protections: z
            .object({
                geoRestriction: z.boolean().optional(),
                accessLevel: z.string().nullable().optional(),
                drmProtection: z.boolean().optional(),
            })
            .optional(),
    })
    .passthrough()
    .superRefine(({ files, fileUrl, activeTab, protections }, ctx) => {
        if (activeTab === TABS.LOCAL && !files) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['files'],
                message: 'Field required',
            });
        }
        if (activeTab === TABS.URL && !fileUrl) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['fileUrl'],
                message: 'Field required',
            });
        }
        if (protections?.drmProtection && !protections?.accessLevel) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['protections', 'accessLevel'],
                message: 'Viewer access is required when DRM is enabled',
            });
        }
    });

type UploadFormData = {
    files: File[];
    fileUrl: string;
    overlay: string;
    fileType: string;
    protections: {
        geoRestriction?: boolean;
        accessLevel?: string | null;
        drmProtection?: boolean;
    };
};

export const useUploadDialogForm = ({ provider, toggleDialog }: { provider: string; toggleDialog: () => void }) => {
    const history = useHistory();
    const dispatch = useDispatch();

    const formApi = useForm<UploadFormData>({
        resolver: zodResolver(uploadFormSchema),
        defaultValues: { fileType: 'video', activeTab: TABS.LOCAL },
    });

    const handleSubmit = formApi.handleSubmit(async (data) => {
        try {
            let uploadedId;
            const { drmProtection, ...protectionOptions } = data.protections ?? {};
            const options = { overlayUrl: data.overlay, drmProtection, protections: protectionOptions };

            if (data.files) {
                const ids = await Promise.all(data.files.map((file) => dispatch(createVideo(provider, file, options))));
                uploadedId = ids.find(Number.isInteger);
            } else if (data.fileUrl) {
                uploadedId = await dispatch(createVideoFromUri(provider, data.fileUrl, options));
            } else if (data.fileType) {
                uploadedId = await dispatch(createVideoWithoutFile(provider, data.fileType));
            }

            if (uploadedId) {
                history.push(`/${provider}/assets/${uploadedId}`);
            }

            toggleDialog();
        } catch {
            Notification.notify.error('An unexpected error occurred.');
        }
    });

    return { formApi, handleSubmit };
};
